import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  TrendingUp, LayoutDashboard, Wallet, Copy, ArrowDownCircle, 
  ArrowUpCircle, Settings, LogOut, Menu, X, User, MessageCircle, Bell
} from "lucide-react";
import { Button } from "./ui/button";
import { formatPrice } from "../lib/utils";
import NotificationBell from "./NotificationBell";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Mercados" },
  { path: "/portfolio", icon: Wallet, label: "Portafolio" },
  { path: "/copy-trading", icon: Copy, label: "Copy Trading" },
  { path: "/deposit", icon: ArrowDownCircle, label: "Depositar" },
  { path: "/withdraw", icon: ArrowUpCircle, label: "Retirar" },
  { path: "/notifications", icon: Bell, label: "Notificaciones" },
  { path: "/profile", icon: User, label: "Mi Perfil" },
];

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [prices, setPrices] = useState([]);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) setUser(JSON.parse(storedUser));
    
    const fetchPrices = async () => {
      try {
        const res = await axios.get(`${API}/crypto/prices`);
        setPrices(res.data.prices?.slice(0, 8) || []);
      } catch (e) {
        console.error(e);
      }
    };
    fetchPrices();
    const interval = setInterval(fetchPrices, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0B0E11] max-w-[100vw] overflow-x-hidden">
      {/* Fixed Header Container for Mobile */}
      <div className="fixed top-0 left-0 right-0 z-50 lg:relative">
        {/* Ticker Tape - Bigger on PC */}
        <div className="bg-[#181A20] border-b border-[#2B3139] py-1 md:py-2 overflow-hidden max-w-[100vw]">
          <div className="ticker-tape">
            <div className="ticker-tape-content opacity-85">
              {[...prices, ...prices, ...prices].map((coin, i) => (
                <div key={i} className="inline-flex items-center gap-1 md:gap-2 mx-3 md:mx-6 flex-shrink-0">
                  <span className="text-[#848E9C] text-[10px] md:text-sm">{coin.symbol}</span>
                  <span className="font-mono text-[10px] md:text-sm text-[#EAECEF]">
                    {formatPrice(coin.current_price)}€
                  </span>
                  <span className={`font-mono text-[9px] md:text-xs ${coin.price_change_percentage_24h >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                    {coin.price_change_percentage_24h >= 0 ? '+' : ''}{coin.price_change_percentage_24h?.toFixed(2).replace('.', ',')}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Header */}
        <header className="glass-header">
          <div className="flex items-center justify-between h-12 md:h-14 px-2 md:px-4">
            <div className="flex items-center gap-2 md:gap-4">
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden text-[#EAECEF]"
                data-testid="mobile-menu-btn"
              >
                {sidebarOpen ? <X className="w-5 h-5 md:w-6 md:h-6" /> : <Menu className="w-5 h-5 md:w-6 md:h-6" />}
              </button>
              <Link to="/dashboard" className="flex items-center gap-1 md:gap-2">
                <div className="w-7 h-7 md:w-8 md:h-8 bg-[#FCD535] rounded-sm flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-[#0B0E11]" />
                </div>
                <span className="text-lg font-bold text-[#EAECEF] hidden sm:block">CopiaTrade.io</span>
              </Link>
            </div>
            
            <div className="flex items-center gap-1 md:gap-3">
              <NotificationBell />
              <Link 
                to="/profile" 
                className="flex items-center gap-2 text-sm hover:bg-[#2B3139] px-2 md:px-3 py-2 rounded-lg transition-colors group"
                data-testid="profile-link"
              >
                {user?.profile_image ? (
                  <img src={user.profile_image} alt="Profile" className="w-6 h-6 md:w-7 md:h-7 rounded-full object-cover border-2 border-[#FCD535]" />
                ) : (
                  <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-[#FCD535] flex items-center justify-center">
                    <User className="w-3 h-3 md:w-4 md:h-4 text-[#0B0E11]" />
                  </div>
                )}
                <span className="text-[#EAECEF] hidden sm:block group-hover:text-[#FCD535]">{user?.full_name}</span>
              </Link>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                className="border-[#F6465D]/50 text-[#F6465D] hover:bg-[#F6465D] hover:text-white px-2 md:px-3 h-8"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 md:mr-1" />
                <span className="hidden md:inline">Salir</span>
              </Button>
            </div>
          </div>
        </header>
      </div>

      {/* Spacer for fixed header on mobile */}
      <div className="h-[88px] lg:hidden"></div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-30 w-64 
          glass-sidebar transform transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          top-[88px] lg:top-0
        `}>
          <nav className="p-4 space-y-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const isCopyTrading = item.path === "/copy-trading";
              
              return (
                <div key={item.path} className="relative">
                  <Link
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded-md transition-colors
                      ${isActive 
                        ? 'bg-[#FCD535]/10 text-[#FCD535] border-l-2 border-[#FCD535]' 
                        : 'text-[#848E9C] hover:bg-[#2B3139] hover:text-[#EAECEF]'
                      }
                      ${isCopyTrading && !isActive ? 'bg-[#0ECB81]/5 border border-[#0ECB81]/30 hover:bg-[#0ECB81]/10 text-[#0ECB81]' : ''}
                    `}
                    data-testid={`nav-${item.path.slice(1)}`}
                  >
                    <item.icon className={`w-5 h-5 ${isCopyTrading && !isActive ? 'text-[#0ECB81]' : ''}`} />
                    <span className={`font-medium ${isCopyTrading && !isActive ? 'text-[#0ECB81]' : ''}`}>{item.label}</span>
                  </Link>
                  
                  {/* Badge superpuesto para Copy Trading */}
                  {isCopyTrading && !isActive && (
                    <div 
                      className="absolute -top-2 right-2 transform rotate-12 pointer-events-none"
                    >
                      <span className="bg-gradient-to-r from-[#0ECB81] to-[#00D68F] text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-lg uppercase tracking-wide">
                        Genera ingresos
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
            
            {user?.is_admin && (
              <Link
                to="/admin-panel"
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-md transition-colors mt-4 border-t border-[#2B3139] pt-6
                  ${location.pathname === '/admin-panel'
                    ? 'bg-[#F6465D]/10 text-[#F6465D] border-l-2 border-[#F6465D]' 
                    : 'text-[#F6465D]/70 hover:bg-[#F6465D]/10 hover:text-[#F6465D]'
                  }
                `}
                data-testid="nav-admin"
              >
                <Settings className="w-5 h-5" />
                <span className="font-medium">Admin Panel</span>
              </Link>
            )}

            {/* Soporte 24/7 */}
            <div className="mt-6 pt-4 border-t border-[#2B3139]">
              <a
                href="https://t.me/Andresmartinbit"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 rounded-md transition-colors bg-gradient-to-r from-[#0088cc]/20 to-[#0088cc]/10 hover:from-[#0088cc]/30 hover:to-[#0088cc]/20 border border-[#0088cc]/30"
                data-testid="contact-support"
              >
                <div className="w-8 h-8 bg-[#0088cc] rounded-full flex items-center justify-center">
                  <MessageCircle className="w-4 h-4 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="font-medium text-[#EAECEF] text-sm">Contactar Soporte</span>
                  <span className="text-[#0ECB81] text-xs font-semibold">24/7 Disponible</span>
                </div>
              </a>
              <p className="text-[#848E9C] text-xs mt-2 px-4">
                @Andresmartinbit
              </p>
            </div>
          </nav>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 top-[88px] bg-black/50 z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-2 min-h-[calc(100vh-8rem)] max-w-[100vw] overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
}
